import React, { useEffect, useState } from 'react';
import PlantSVG from '../Plant/PlantSVG';
import { formatMinutes } from '../../utils/timeUtils';
import { calculateLevel, getLevelTitle } from '../../utils/xpUtils';

const CONFETTI_COLORS = ['#ef4444', '#f87171', '#ffd54f', '#ff7043', '#42a5f5'];

export default function SessionCelebration({ session, streak, totalXP, onContinue, showUpgradeHint = false }) {
  const [showConfetti, setShowConfetti] = useState(true);
  const levelInfo = calculateLevel(totalXP);

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (!session) return null;

  return (
    <div
      role="dialog"
      aria-label="Session complete"
      className="relative max-w-md mx-auto px-6 py-8 pb-32 flex flex-col items-center gap-5 overflow-hidden"
    >
      {showConfetti && (
        <div className="fixed inset-0 z-[100] pointer-events-none" aria-hidden="true">
          {Array.from({ length: 20 }, (_, i) => (
            <span
              key={i}
              className={`confetti-particle absolute -top-2.5 ${i % 2 === 0 ? 'w-1.5 h-2.5 rounded-sm' : 'w-2 h-1.5 rounded-full'}`}
              style={{
                left: `${Math.random() * 100}%`,
                background: CONFETTI_COLORS[i % 5],
                animation: `confettiFall 3s ease-in ${Math.random()}s forwards`,
              }}
            />
          ))}
        </div>
      )}
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
          70% { opacity: 0.8; }
          100% { transform: translateY(100vh) rotate(1080deg) scale(0.3); opacity: 0; }
        }
      `}</style>

      <div className="drop-shadow-[0_0_20px_rgba(239,68,68,0.2)]">
        <PlantSVG species={session.plantType || session.treeType || 'roma'} progress={1} size={200} />
      </div>

      <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-br from-[color:var(--color-text-primary)] to-[color:var(--color-accent-light)] bg-clip-text text-transparent">
        Tomato Harvested!
      </h2>

      <div className="grid grid-cols-3 gap-3 w-full">
        <div className="glass-card p-3 text-center space-y-0.5">
          <span className="block text-lg font-bold text-[color:var(--color-text-primary)]">{formatMinutes(session.durationMinutes)}</span>
          <span className="block text-[10px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold">Focused</span>
        </div>
        <div className="glass-card p-3 text-center space-y-0.5">
          <span className="block text-lg font-bold text-amber-500 dark:text-amber-400">+{session.xpEarned}</span>
          <span className="block text-[10px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold">XP Earned</span>
        </div>
        <div className="glass-card p-3 text-center space-y-0.5">
          <span className="block text-lg font-bold text-orange-500 dark:text-orange-400">{streak.count}</span>
          <span className="block text-[10px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold">Day Streak</span>
        </div>
      </div>

      <div className="w-full glass-card p-3 space-y-2">
        <div className="flex items-center justify-between text-xs text-[color:var(--color-text-secondary)]">
          <span className="font-semibold">Level {levelInfo.level} — {getLevelTitle(levelInfo.level)}</span>
          <span className="text-[color:var(--color-text-muted)]">{levelInfo.progressXP}/{levelInfo.requiredXP} XP</span>
        </div>
        <div className="h-2 rounded-full bg-[color:var(--color-bg-elevated)] overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-tomato-500 to-orange-400 transition-[width] duration-700"
            style={{ width: `${Math.round(levelInfo.progress * 100)}%` }}
          />
        </div>
      </div>

      <div className="w-full space-y-2">
        <p className="text-sm text-[color:var(--color-text-secondary)] text-center">What did you focus on?</p>
        <input
          type="text"
          className="w-full px-4 py-2.5 rounded-xl bg-[color:var(--glass-bg)] border border-[color:var(--glass-border)] text-[color:var(--color-text-primary)] placeholder-[color:var(--color-text-muted)] focus:outline-none focus:border-[color:var(--color-accent)] transition-colors text-sm"
          placeholder="e.g., Chapter 5 of my textbook"
          maxLength={100}
          aria-label="What did you focus on?"
        />
      </div>

      {showUpgradeHint && (
        <p className="text-xs text-center text-[color:var(--color-text-muted)] italic">
          Unlock more plant varieties and detailed analytics with Premium
        </p>
      )}

      <button
        type="button"
        onClick={onContinue}
        className="w-full px-8 py-3.5 rounded-xl bg-gradient-to-br from-tomato-700 via-tomato-500 to-tomato-500 text-[color:var(--color-bg-primary)] dark:text-soil-300 text-lg font-bold tracking-tight shadow-glow-md hover:shadow-glow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-300"
      >
        Continue
      </button>
    </div>
  );
}
